import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Expense } from '@/types/expense';

export const useExpenses = () => {
  const [expenses, setExpenses] = useState<Expense[]>(() => {
    const savedExpenses = localStorage.getItem('expenses');
    return savedExpenses ? JSON.parse(savedExpenses) : [];
  });

  // Listen for storage changes from other components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'expenses') {
        const newExpenses = e.newValue ? JSON.parse(e.newValue) : [];
        setExpenses(newExpenses);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for custom expense updates
  useEffect(() => {
    const handleExpenseUpdate = (e: CustomEvent) => {
      setExpenses(e.detail);
    };

    window.addEventListener('expensesUpdated', handleExpenseUpdate as EventListener);
    return () => window.removeEventListener('expensesUpdated', handleExpenseUpdate as EventListener);
  }, []);

  useEffect(() => {
    localStorage.setItem('expenses', JSON.stringify(expenses));
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('expensesUpdated', { detail: expenses }));
  }, [expenses]);

  const addExpense = (expense: Omit<Expense, 'id'>) => {
    const newExpense = {
      ...expense,
      id: uuidv4()
    };
    setExpenses(prev => {
      const updatedExpenses = [...prev, newExpense];
      // Trigger immediate update
      window.dispatchEvent(new CustomEvent('expensesUpdated', { detail: updatedExpenses }));
      return updatedExpenses;
    });
    return newExpense;
  };

  const deleteExpense = (id: string) => {
    setExpenses(prev => {
      const updatedExpenses = prev.filter(expense => expense.id !== id);
      // Trigger immediate update
      window.dispatchEvent(new CustomEvent('expensesUpdated', { detail: updatedExpenses }));
      return updatedExpenses;
    });
  };

  const clearExpenses = () => {
    setExpenses([]);
    localStorage.removeItem('expenses');
    // Trigger immediate update
    window.dispatchEvent(new CustomEvent('expensesUpdated', { detail: [] }));
  };

  return {
    expenses,
    addExpense,
    deleteExpense,
    clearExpenses
  };
};